import { useMemo } from "react";
import { t } from "@lingui/macro";
import SprintGraph from "./Graphs/NMGraph";
import AttackGraph from "./Graphs/ACGraph";
import BreakawayGraph from "./Graphs/MAPGraph";
import EndureGraph from "./Graphs/FTPGraph";
import PotentialArrow from "./Graphs/PotentialArrow";
import { EducationStepContent } from "../types";
import { FourDPLogoWithCopySvg } from "./FourDPLogoWithCopySvg";

export function useFourDPSteps(activeStep: number, isStandAlone: boolean): EducationStepContent[] {
  const DTL = "Dimensional Training Load (DTL)";
  const FITNESS_SCORE = "Fitness Score";
  const TC = "Training Capacity";
  const AC = "Anaerobic Capacity (AC)";
  const AC_ = "Anaerobic Capacity";
  const MAP = "Maximum Aerobic Power (MAP)";
  const MAP_ = "Maximum Aerobic Power";
  const MAP_ONLY = "MAP";
  const FTP_ONLY = "FTP";
  const NM = "Neuromuscular Power (NM)";
  const NM_ = "Neuromuscular Power";
  const FTP = "Functional Threshold Power (FTP)";
  const FTP_ = "Functional Threshold Power";
  return useMemo(
    () => [
      {
        stepId: 0,
        stepName: "intro",
        headline: t`Intro to the core metrics of Athlete Profile.`,
        component: <FourDPLogoWithCopySvg activeStep={activeStep} stepId={0} key={0} />,
        skippable: false,
        onContinue: () => {},
        disabled: false,
        buttonName: t`Next`,
        description: t`Your Cycling Athlete Profile measures 4 metrics to determine your strengths and weaknesses. It is used to calibrate workouts and give you the most accurate ${DTL}, ${FITNESS_SCORE} and ${TC}.`,
        headerTitle: isStandAlone ? t`Metrics explained` : t`Estimate your Athlete Profile`,
        ticks: [],
      },
      {
        stepId: 1,
        stepName: "sprint",
        headline: NM,
        component: <SprintGraph activeStep={activeStep} stepId={1} key={1} />,
        skippable: false,
        onContinue: () => {},
        disabled: false,
        buttonName: t`Next`,
        headerTitle: t`Metrics explained`,
        description: t`${NM_}, measures your max power in a 5-second all-out effort, balancing force and cadence. It reveals sprinter traits and pedaling efficiency needs.`,
        power: t`5-Seconds Max Power`,
        ticks: [
          {
            label: t`Time`,
            height: 20,
            labelVariant: "ui-sm",
          },
          ...Array.from({ length: 4 }, () => ({
            height: 9,
          })),
          {
            label: t`5 sec`,
            height: 20,
            labelVariant: "ui-sm-bold",
          },
        ],
      },
      {
        stepId: 2,
        stepName: "attack",
        headline: AC,
        component: <AttackGraph activeStep={activeStep} stepId={2} key={2} />,
        skippable: false,
        onContinue: () => {},
        disabled: false,
        buttonName: t`Next`,
        headerTitle: t`Metrics explained`,
        description: t`${AC_}, determined through a 1-minute effort completed while fatigued, is a combination of your maximal capacity and your ability to recover from hard, repeated efforts.`,
        power: t`1-Min Repeatable Power`,
        ticks: [
          {
            label: t`Time`,
            height: 20,
            labelVariant: "ui-sm",
          },
          {
            height: 20,
            label: t`1 min`,
            labelVariant: "ui-sm-bold",
          },
          {
            height: 20,
            label: t`2 min`,
            labelVariant: "ui-sm-bold",
          },
          {
            height: 20,
            label: t`3 min`,
            labelVariant: "ui-sm-bold",
          },
        ],
      },
      {
        stepId: 3,
        stepName: "breakaway",
        headline: MAP,
        component: <BreakawayGraph activeStep={activeStep} stepId={3} key={3} />,
        skippable: false,
        onContinue: () => {},
        disabled: false,
        buttonName: t`Next`,
        headerTitle: t`Metrics explained`,
        description: t`${MAP_}, represents the highest power you can sustain for 5 minutes, synonymous with your VO2 Max. Without a high ${MAP_ONLY}, your ability to raise your threshold is limited, one reason why focusing solely on ${FTP_ONLY} limits your potential.`,
        power: t`5-min Fresh Power`,
        ticks: [
          {
            label: t`Time`,
            height: 20,
            labelVariant: "ui-sm",
          },
          ...Array.from({ length: 4 }, () => ({
            height: 9,
          })),
          {
            label: t`5 min`,
            height: 20,
            labelVariant: "ui-sm-bold",
          },
        ],
      },
      {
        stepId: 4,
        stepName: "endure",
        headline: FTP,
        component: <EndureGraph activeStep={activeStep} stepId={4} key={4} />,
        skippable: false,
        onContinue: () => {},
        disabled: false,
        buttonName: isStandAlone ? t`Close` : t`Start questionnaire`,
        headerTitle: t`Metrics explained`,
        description: t`${FTP_}, is the highest power you can sustain for an hour and has traditionally been seen as the ultimate performance metric. However, at Wahoo, we believe a true understanding of your athletic abilities requires looking beyond FTP alone.`,
        power: t`60-min Power`,
        endAdornment: <PotentialArrow />,
        ticks: [
          {
            label: t`Time`,
            height: 20,
            labelVariant: "ui-sm",
          },
          ...Array.from({ length: 11 }, () => ({
            height: 9,
          })),
          {
            label: t`60 min`,
            height: 20,
            labelVariant: "ui-sm-bold",
          },
        ],
      },
    ],
    [activeStep, isStandAlone]
  );
}
